// MCHD Room Scheduler - Building Management Component
// Main interface for managing buildings
// Updated: 08-10-2025 02:16:29 - Added URL sync for tree view and room detail sidebar

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Title,
  Paper,
  Tabs,
  Text,
  Group,
  Button,
  Stack,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import { IconBuilding, IconPlus, IconRefresh } from '@tabler/icons-react';
import { useCreateBuilding, useDeleteBuilding } from '../../../hooks/admin/useBuildings';
import { BuildingForm } from '@/components/forms/BuildingForm';
import { BuildingList } from '@/components/organisms/BuildingList';
import { BuildingStats } from '@/components/organisms/BuildingStats';
import { BuildingTreeView } from '../../../components/organisms/BuildingTreeView';
import { RoomDetailSidebar } from '../../../components/drawers';
import { useBuildingTree } from '../../../hooks/coordinator/useBuildingTree';
import { CreateFloorModal } from '../../../components/modals';

// =============================================================================
// TYPES
// =============================================================================

interface BuildingManagementProps {
  className?: string;
}

// =============================================================================
// TREE VIEW TAB COMPONENT
// =============================================================================
// Removed BuildingTreeViewTab wrapper - now directly using BuildingTreeView in Tabs.Panel
// with onRoomClick callback for sidebar integration

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Building Management Component
 * Provides interface for managing buildings with CRUD operations
 * 
 * Modified: 07-10-2025 14:01:32 - Added hierarchical tree view (Gap G3-1)
 * @param props - Component props
 * @returns Building management interface
 */
export function BuildingManagement({ className }: BuildingManagementProps): React.ReactElement {
  // =============================================================================
  // URL SYNC STATE MANAGEMENT
  // =============================================================================
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial state from URL
  const viewParam = searchParams.get('view');
  const roomIdParam = searchParams.get('roomId');
  
  // =============================================================================
  // STATE
  // =============================================================================
  
  const [activeTab, setActiveTab] = useState<string>(viewParam === 'tree' ? 'tree' : 'list');
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(roomIdParam);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showCreateFloorModal, setShowCreateFloorModal] = useState(false);
  const [selectedBuildingForFloor, setSelectedBuildingForFloor] = useState<any | null>(null);
  const [filters, setFilters] = useState<{
    search: string;
    sortBy: 'name' | 'createdAt' | 'updatedAt';
    sortOrder: 'asc' | 'desc';
  }>({
    search: '',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // =============================================================================
  // URL SYNCHRONIZATION EFFECT
  // =============================================================================
  
  /**
   * Sync URL parameters to component state
   * Enables bookmarkable URLs and browser back/forward navigation
   */
  useEffect(() => {
    const view = searchParams.get('view');
    const roomId = searchParams.get('roomId');
    
    // Update view state from URL
    if (view === 'tree') {
      setActiveTab('tree');
    } else if (view === null) {
      setActiveTab('list');
    }
    
    // Update room selection from URL
    if (roomId) {
      setSelectedRoomId(roomId);
    } else {
      setSelectedRoomId(null);
    }
  }, [searchParams]);

  // =============================================================================
  // DATA FETCHING
  // =============================================================================
  
  // Fetch building tree data for both list and tree views
  const {
    data: buildingTree,
    isLoading: isLoadingTree,
    error: treeError,
    refetch: refetchBuildings,
  } = useBuildingTree();

    // Transform tree data for list view
    const buildingsData = buildingTree ? {
      buildings: buildingTree.map(building => ({
        id: building.id,
        name: building.name,
        description: null, // Not available in tree data
        isActive: building.isActive,
        createdAt: new Date().toISOString(), // Not available in tree data
        updatedAt: new Date().toISOString(), // Not available in tree data
        _count: { floors: building.floors.length },
        roomsCount: building.floors.reduce((total, floor) => total + floor.rooms.length, 0),
        holidaysCount: 0, // This would need to be fetched separately if needed
      })),
      pagination: {
        page: 1,
        limit: 50,
        total: buildingTree.length,
        totalPages: 1,
      }
    } : null;

  const createBuildingMutation = useCreateBuilding();
  const deleteBuildingMutation = useDeleteBuilding();

  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle building creation
   * @param data - Building data
   */
  const handleCreateBuilding = async (data: {
    name: string;
    description?: string | null;
    isActive?: boolean;
  }) => {
    try {
      await createBuildingMutation.mutateAsync(data);
      setShowCreateForm(false);
      await refetchBuildings();
    } catch (error) {
      console.error('Error creating building:', error);
    }
  };

  /**
   * Handle building deletion
   * @param id - Building ID
   */
  const handleDeleteBuilding = async (id: string) => {
    try {
      await deleteBuildingMutation.mutateAsync(id);
      await refetchBuildings();
    } catch (error) {
      console.error('Error deleting building:', error);
    }
  };

  /**
   * Handle add floor button click
   * Opens CreateFloorModal with selected building context
   * Created: 09-10-2025 13:26:21
   * @param building - Building data
   */
  const handleAddFloor = (building: any) => {
    setSelectedBuildingForFloor(building);
    setShowCreateFloorModal(true);
  };

  /**
   * Handle building selection
   * @param id - Building ID
   */
  const handleSelectBuilding = (id: string) => {
    setSelectedBuilding(id);
    setActiveTab('details');
  };

  /**
   * Handle filter changes
   * @param newFilters - New filter values
   */
  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  /**
   * Handle tab change with URL synchronization
   * @param value - Tab value ('list' | 'tree' | 'details')
   */
  const handleTabChange = (value: string | null) => {
    if (!value) return;
    
    const newParams = new URLSearchParams(searchParams);
    
    if (value === 'list') {
      // List view is default - remove params
      newParams.delete('view');
      newParams.delete('roomId');
    } else if (value === 'tree') {
      // Tree view - set view param
      newParams.set('view', 'tree');
      // Keep roomId if already selected
    } else if (value === 'details') {
      // Keep current params
    }
    
    setActiveTab(value);
    setSearchParams(newParams);
  };

  /**
   * Handle room click from tree view
   * Opens room detail sidebar and updates URL
   * @param roomId - Room ID to display
   */
  const handleRoomClick = (roomId: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', 'tree'); // Ensure tree view is active
    newParams.set('roomId', roomId);
    
    setSelectedRoomId(roomId);
    setSearchParams(newParams);
  };

  /**
   * Handle closing room detail sidebar
   * Removes roomId from URL but keeps tree view active
   */
  const handleCloseSidebar = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('roomId');
    
    setSelectedRoomId(null);
    setSearchParams(newParams);
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  if (isLoadingTree) {
    return (
      <Container size="xl" {...(className && { className })}>
        <Center h={400}>
          <Stack align="center">
            <Loader size="lg" />
            <Text>Loading buildings...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  if (treeError) {
    return (
      <Container size="xl" {...(className && { className })}>
        <Alert color="red" title="Error Loading Buildings">
          {treeError instanceof Error ? treeError.message : String(treeError)}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" {...(className && { className })}>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <IconBuilding size={32} />
            <Title order={1}>Building Management</Title>
          </Group>
          
          <Group gap="sm">
            <Button
              variant="outline"
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetchBuildings()}
              loading={isLoadingTree}
            >
              Refresh
            </Button>
            
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setShowCreateForm(true)}
            >
              Create Building
            </Button>
          </Group>
        </Group>

        {/* Statistics */}
        {buildingsData && (
          <BuildingStats buildings={buildingsData.buildings} />
        )}

        {/* Main Content */}
        <Paper shadow="sm" p="md" radius="md">
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tabs.List>
              <Tabs.Tab value="list">Building List</Tabs.Tab>
              <Tabs.Tab value="tree">Tree View</Tabs.Tab>
              {selectedBuilding && (
                <Tabs.Tab value="details">Building Details</Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel value="list" pt="md">
              <BuildingList
                buildings={buildingsData?.buildings || []}
                pagination={buildingsData?.pagination || undefined}
                filters={filters}
                onFilterChange={handleFilterChange}
                onSelectBuilding={handleSelectBuilding}
                onDeleteBuilding={handleDeleteBuilding}
                isLoading={isLoadingTree}
                onEditBuilding={(id: string) => {
                  setSelectedBuilding(id);
                  setShowEditForm(true);
                }}
              />
            </Tabs.Panel>

            <Tabs.Panel value="tree" pt="md">
              <BuildingTreeView
                buildings={buildingTree || []}
                isLoading={isLoadingTree}
                error={treeError ? String(treeError) : null}
                onRoomClick={handleRoomClick}
                onAddFloor={handleAddFloor}
              />
            </Tabs.Panel>

            {selectedBuilding && (
              <Tabs.Panel value="details" pt="md">
                <BuildingDetails
                  buildingId={selectedBuilding}
                  onEdit={() => setShowEditForm(true)}
                  onDelete={handleDeleteBuilding}
                />
              </Tabs.Panel>
            )}
          </Tabs>
        </Paper>

        {/* Create Building Form */}
        {showCreateForm && (
          <BuildingForm
            mode="create"
            onSubmit={handleCreateBuilding}
            onCancel={() => setShowCreateForm(false)}
            isLoading={createBuildingMutation.isPending}
          />
        )}

        {/* Edit Building Form */}
        {showEditForm && selectedBuilding && (
          <BuildingForm
            mode="edit"
            buildingId={selectedBuilding}
            onSubmit={handleCreateBuilding}
            onCancel={() => setShowEditForm(false)}
            isLoading={createBuildingMutation.isPending}
          />
        )}
      </Stack>

      {/* Room Detail Sidebar - Opens when room is clicked in tree view */}
      <RoomDetailSidebar
        roomId={selectedRoomId}
        opened={!!selectedRoomId}
        onClose={handleCloseSidebar}
      />

      {/* Create Floor Modal */}
      {selectedBuildingForFloor && (
        <CreateFloorModal
          opened={showCreateFloorModal}
          onClose={() => {
            setShowCreateFloorModal(false);
            setSelectedBuildingForFloor(null);
          }}
          buildingId={selectedBuildingForFloor.id}
          buildingName={selectedBuildingForFloor.name}
          existingFloorNumbers={
            selectedBuildingForFloor.floors?.map((f: any) => f.number) || []
          }
          onSuccess={() => {
            refetchBuildings();
            setShowCreateFloorModal(false);
            setSelectedBuildingForFloor(null);
          }}
        />
      )}
    </Container>
  );
}

// =============================================================================
// BUILDING DETAILS COMPONENT
// =============================================================================

interface BuildingDetailsProps {
  buildingId: string;
  onEdit: () => void;
  onDelete: (id: string) => void;
}

/**
 * Building Details Component
 * Shows detailed information about a specific building
 */
function BuildingDetails({ buildingId, onEdit, onDelete }: BuildingDetailsProps): React.ReactElement {
  // This would be implemented with useBuildingById hook
  // For now, showing a placeholder
  
  return (
    <Stack gap="md">
      <Text>Building Details for ID: {buildingId}</Text>
      <Group>
        <Button onClick={onEdit}>Edit Building</Button>
        <Button color="red" onClick={() => onDelete(buildingId)}>
          Delete Building
        </Button>
      </Group>
    </Stack>
  );
}
